import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import type { ReplyEmailItem } from "hooks/use-email-replies-api";
import { useSalesPipelines } from "hooks/use-sales-pipelines-api";
import { useTalents } from "hooks/use-talents-api";
import { RestApiError, restClient } from "lib/rest-client";
import { type FC, useState } from "react";
import { toast } from "react-toastify";
import { toastError } from "utils/toast-utils";

type CrmLinkAction =
  | "create_talent"
  | "create_pipeline"
  | "link_talent"
  | "link_pipeline";

type LinkCrmResponse = {
  replyId: string;
  talentId: string | null;
  salesPipelineId: string | null;
  createdTalent: boolean;
  createdPipeline: boolean;
};

export type CrmLinkDialogProps = {
  isOpen: boolean;
  reply: ReplyEmailItem | null;
  onClose: () => void;
  onLinked: () => void;
};

/**
 * 返信からの CRM 連携ダイアログ。4 アクションを提供。
 * @param {CrmLinkDialogProps} props props
 * @return {JSX.Element|null} dialog
 */
export const CrmLinkDialog: FC<CrmLinkDialogProps> = ({
  isOpen,
  reply,
  onClose,
  onLinked,
}) => {
  if (!isOpen || !reply) return null;
  return (
    <CrmLinkDialogBody reply={reply} onClose={onClose} onLinked={onLinked} />
  );
};

type CrmLinkDialogBodyProps = {
  reply: ReplyEmailItem;
  onClose: () => void;
  onLinked: () => void;
};

/**
 * CrmLinkDialog の本体。isOpen ガード後にのみ mount され、
 * useSalesPipelines / useTalents の fetch をダイアログ表示時のみに限定する。
 * @param {CrmLinkDialogBodyProps} props props
 * @return {JSX.Element} dialog body
 */
const CrmLinkDialogBody: FC<CrmLinkDialogBodyProps> = ({
  reply,
  onClose,
  onLinked,
}) => {
  const [action, setAction] = useState<CrmLinkAction>("link_pipeline");
  const [talentId, setTalentId] = useState("");
  const [pipelineId, setPipelineId] = useState("");
  const [talentName, setTalentName] = useState("");
  const [matterTitle, setMatterTitle] = useState("");
  const [clientCompany, setClientCompany] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { pipelines } = useSalesPipelines({ limit: 100 });
  const { talents } = useTalents({ limit: 100 });

  /**
   * ダイアログを閉じて状態リセット
   * @return {void}
   */
  const handleClose = () => {
    setAction("link_pipeline");
    setTalentId("");
    setPipelineId("");
    setTalentName(reply.fromName ?? "");
    setMatterTitle(reply.subject);
    setClientCompany("");
    onClose();
  };

  /**
   * API 呼び出し
   * @return {Promise<void>}
   */
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = { action };
      if (action === "link_pipeline") {
        body.salesPipelineId = pipelineId;
      } else if (action === "link_talent") {
        body.talentId = talentId;
        body.pipelineData = {
          matterTitle: matterTitle || null,
          clientCompany: clientCompany || null,
        };
      } else if (action === "create_talent") {
        body.talentData = {
          name: talentName || reply.fromName || reply.fromEmail,
          email: reply.fromEmail,
        };
        body.pipelineData = {
          matterTitle: matterTitle || null,
          clientCompany: clientCompany || null,
        };
      } else if (action === "create_pipeline") {
        body.talentId = talentId;
        body.pipelineData = {
          matterTitle: matterTitle || null,
          clientCompany: clientCompany || null,
        };
      }

      await restClient.post<LinkCrmResponse>(
        `/emails/replies/${reply.id}/link-crm`,
        body,
      );
      toast.success("CRM 連携しました");
      onLinked();
      handleClose();
    } catch (e) {
      const detail =
        e instanceof RestApiError ? e.message : "CRM 連携に失敗しました";
      toastError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-xl">
        <h3 className="font-bold text-base mb-3">CRM 連携</h3>
        <div className="text-xs text-slate-500 mb-3">
          対象: {reply.fromName || reply.fromEmail} /{" "}
          {reply.subject || "(件名なし)"}
        </div>

        {/* アクション選択 */}
        <div className="mb-3">
          <div className="text-xs font-semibold mb-1">アクション</div>
          <div className="flex flex-wrap gap-1">
            {(
              [
                ["link_pipeline", "既存パイプラインに紐付け"],
                ["link_talent", "既存タレント + 新規パイプライン"],
                ["create_talent", "新規タレント + 新規パイプライン"],
                ["create_pipeline", "既存タレント + 新規パイプライン"],
              ] as Array<[CrmLinkAction, string]>
            ).map(([value, label]) => (
              <Button
                key={value}
                type="button"
                size="xs"
                variant={action === value ? "default" : "outline"}
                onClick={() => setAction(value)}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>

        {/* link_pipeline */}
        {action === "link_pipeline" && (
          <div className="mb-3">
            <div className="text-xs font-semibold mb-1">パイプライン</div>
            <select
              className="select select-bordered select-sm w-full"
              value={pipelineId}
              onChange={e => setPipelineId(e.target.value)}
            >
              <option value="">選択してください</option>
              {pipelines.map(p => (
                <option key={p.id} value={p.id}>
                  {p.talentName} / {p.matterTitle || p.clientCompany || "-"}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* link_talent / create_pipeline */}
        {(action === "link_talent" || action === "create_pipeline") && (
          <div className="mb-3">
            <div className="text-xs font-semibold mb-1">タレント</div>
            <select
              className="select select-bordered select-sm w-full"
              value={talentId}
              onChange={e => setTalentId(e.target.value)}
            >
              <option value="">選択してください</option>
              {talents.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* create_talent */}
        {action === "create_talent" && (
          <div className="mb-3">
            <div className="text-xs font-semibold mb-1">タレント名</div>
            <input
              type="text"
              className="input input-bordered input-sm w-full"
              value={talentName}
              onChange={e => setTalentName(e.target.value)}
              placeholder={reply.fromName || reply.fromEmail}
            />
          </div>
        )}

        {/* pipeline details (全ての作成系) */}
        {action !== "link_pipeline" && (
          <>
            <div className="mb-2">
              <div className="text-xs font-semibold mb-1">案件タイトル</div>
              <input
                type="text"
                className="input input-bordered input-sm w-full"
                value={matterTitle}
                onChange={e => setMatterTitle(e.target.value)}
                placeholder={reply.subject}
              />
            </div>
            <div className="mb-3">
              <div className="text-xs font-semibold mb-1">企業名</div>
              <input
                type="text"
                className="input input-bordered input-sm w-full"
                value={clientCompany}
                onChange={e => setClientCompany(e.target.value)}
              />
            </div>
          </>
        )}

        <div className="modal-action">
          <Button type="button" variant="ghost" onClick={handleClose}>
            キャンセル
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={
              submitting || !isReady(action, pipelineId, talentId, talentName)
            }
          >
            {submitting ? <Loading size="xs" /> : "実行"}
          </Button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={handleClose} />
    </div>
  );
};

/**
 * 実行可能条件を満たしているか
 * @param {CrmLinkAction} action アクション
 * @param {string} pipelineId 選択済みパイプライン ID
 * @param {string} talentId 選択済みタレント ID
 * @param {string} talentName 入力済みタレント名
 * @return {boolean} 実行可能かどうか
 */
function isReady(
  action: CrmLinkAction,
  pipelineId: string,
  talentId: string,
  talentName: string,
): boolean {
  if (action === "link_pipeline") return Boolean(pipelineId);
  if (action === "link_talent" || action === "create_pipeline")
    return Boolean(talentId);
  if (action === "create_talent") return Boolean(talentName);
  return false;
}
